import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Check, ChevronUp, ChevronDown } from 'lucide-react';
import { transactionCategoriesApi } from '../lib/api';

interface ManageCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BUSINESS_AREAS = [
  { value: 'eventi_single', label: 'Eventi Single' },
  { value: 'agenzia_amore', label: 'Agenzia Amore' },
  { value: 'dating_coach', label: 'Dating Coach' },
  { value: 'viaggi_single', label: 'Viaggi per Single' }
];

const ManageCategoriesModal: React.FC<ManageCategoriesModalProps> = ({
  isOpen,
  onClose
}) => {
  const [selectedBusinessArea, setSelectedBusinessArea] = useState('eventi_single');
  const [selectedType, setSelectedType] = useState<'income' | 'expense'>('income');
  const [categories, setCategories] = useState<any[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen, selectedBusinessArea, selectedType]);

  const loadCategories = async () => {
    try {
      const data = await transactionCategoriesApi.getByBusinessArea(selectedBusinessArea, selectedType);
      setCategories(data || []);
      setError('');
    } catch (err: any) {
      console.error('Error loading categories:', err);
      setError(err.message || 'Errore nel caricare le categorie');
      setCategories([]);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('Inserisci il nome della categoria');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await transactionCategoriesApi.create({
        business_area: selectedBusinessArea,
        type: selectedType,
        name: newCategoryName.trim()
      });
      setNewCategoryName('');
      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Errore nel creare la categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (id: string) => {
    if (!editingName.trim()) {
      setError('Inserisci il nome della categoria');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await transactionCategoriesApi.update(id, editingName.trim());
      setEditingId(null);
      setEditingName('');
      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Errore nel modificare la categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa categoria?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await transactionCategoriesApi.delete(id);
      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Errore nell\'eliminare la categoria');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (category: any) => {
    setEditingId(category.id);
    setEditingName(category.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    setLoading(true);
    setError('');

    try {
      const currentCategory = categories[index];
      const previousCategory = categories[index - 1];

      await transactionCategoriesApi.updateOrder(currentCategory.id, previousCategory.display_order);
      await transactionCategoriesApi.updateOrder(previousCategory.id, currentCategory.display_order);

      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Errore nello spostare la categoria');
    } finally {
      setLoading(false);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === categories.length - 1) return;

    setLoading(true);
    setError('');

    try {
      const currentCategory = categories[index];
      const nextCategory = categories[index + 1];

      await transactionCategoriesApi.updateOrder(currentCategory.id, nextCategory.display_order);
      await transactionCategoriesApi.updateOrder(nextCategory.id, currentCategory.display_order);

      await loadCategories();
    } catch (err: any) {
      setError(err.message || 'Errore nello spostare la categoria');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const businessAreaLabel = BUSINESS_AREAS.find(ba => ba.value === selectedBusinessArea)?.label || '';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-semibold text-gray-900">
            Gestisci Categorie di Transazione
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Area di Business
              </label>
              <select
                value={selectedBusinessArea}
                onChange={(e) => setSelectedBusinessArea(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {BUSINESS_AREAS.map((area) => (
                  <option key={area.value} value={area.value}>
                    {area.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'income' | 'expense')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="income">Entrate</option>
                <option value="expense">Uscite</option>
              </select>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">
              {businessAreaLabel} - {selectedType === 'income' ? 'Entrate' : 'Uscite'}
            </h3>

            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                placeholder="Nuova categoria..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleAddCategory}
                disabled={loading || !newCategoryName.trim()}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Aggiungi
              </button>
            </div>

            <div className="space-y-2">
              {categories.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Nessuna categoria disponibile
                </p>
              ) : (
                categories.map((category, index) => (
                  <div
                    key={category.id}
                    className="bg-white rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center gap-2 p-3">
                      {editingId === category.id ? (
                        <>
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleUpdateCategory(category.id)}
                            className="flex-1 px-3 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            autoFocus
                          />
                          <button
                            onClick={() => handleUpdateCategory(category.id)}
                            disabled={loading}
                            className="p-2 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={loading}
                            className="p-2 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-50"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <div className="flex flex-col mr-2">
                            <button
                              onClick={() => handleMoveUp(index)}
                              disabled={loading || index === 0}
                              className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Sposta su"
                            >
                              <ChevronUp className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleMoveDown(index)}
                              disabled={loading || index === categories.length - 1}
                              className="p-1 text-gray-600 hover:bg-gray-50 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Sposta giù"
                            >
                              <ChevronDown className="h-4 w-4" />
                            </button>
                          </div>
                          <span className="flex-1 text-sm text-gray-900">
                            {category.name}
                          </span>
                          {category.is_default && (
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded mr-2">
                              Default
                            </span>
                          )}
                          <button
                            onClick={() => startEditing(category)}
                            disabled={loading}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title="Modifica categoria"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            disabled={loading}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title="Elimina categoria"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCategoriesModal;
